import { useTranslator } from "@eo-locale/react";
import * as EsriGeoCode from "esri-leaflet-geocoder";
import * as gj from "geojson-utils";
import * as L from "leaflet";
import { isEmpty } from "lodash";

import {
  KeyboardEvent,
  ReactNode,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef
} from "react";

import usePinFlow from "@/hooks/places/use-pin-flow";
import useUser from "@/hooks/useUser";
import {
  clearAllErrors,
  resetAnswers
} from "@/redux/features/place-form/slice";
import {
  resetIsMarkerInsidePolygon,
  resetMarker,
  resetMarkerError,
  setIsMarkerInsidePolygon,
  setMarker
} from "@/redux/features/place-marker/slice";
import { resetFilterCategories } from "@/redux/features/place-markers/slice";
import { setView } from "@/redux/features/place-right-panel/slice";
import {
  setMapCenterCoordinates,
  setPinDetails
} from "@/redux/features/place/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { Box, useMediaQuery, useToast } from "@/storybook-components";
import { esriToken, toastDuration } from "../constants";
import { EsriContext } from "../context/esri-context";
import type { EsriContextType } from "../context/esri-context-type";
import { isAnyMarkerInsidePolygon } from "../utils/helpers";
import MobilePinActions from "./create-edit-pin/mobile-pin-actions";
import EsriDrawer from "./esri-drawer";
import Interactions from "./interactions/interactions";
import UnverifiedUserModal from "./unverified-user-modal";
import { setUnverifiedModalOpen } from "@/redux/features/unverified-modal/slice";
import useShowUnverifiedModal from "@/hooks/places/use-unverfied-modal";

interface ESRIPanelProps {
  children: ReactNode;
  isOpen: boolean;
  handleCloseDrawer: () => void;
  onOpen: () => void;
  symbols: any;
  rightPanelProps: any;
}

const EsriSidePanel = ({
  children,
  isOpen,
  onOpen,
  handleCloseDrawer,
  symbols,
  rightPanelProps,
  ...rest
}: ESRIPanelProps): JSX.Element => {
  const [isDesktop] = useMediaQuery("(min-width: 768px)");

  const { isEditMode } = useAppSelector((state) => state.placesMarkerSlice);
  const {
    mapRef,
    setInteractionsVisibility,
    showInteractions,
    isActionsVisible,
    setActionsVisibility,
    setNextModalAction,
    openUnsavedModal
  } = useContext(EsriContext) as EsriContextType;
  const { translate } = useTranslator();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const dispatch = useAppDispatch();
  const { editMarker, validateSurveyQuestions } = usePinFlow();
  const { marker } = useAppSelector((state) => state.placesMarkerSlice);
  const { isModalOpen: isUnverifiedModalOpen } = useAppSelector(
    (state) => state.unverifiedModal
  );

  const activityRef = useRef(null);
  const wrapperComp = useRef("wrapper-component");

  const isPinInsidePolygon = useAppSelector(
    (state) => state.placesMarkerSlice.isMarkerInsidePolygon
  );

  const centerCoordinates = useAppSelector(
    (state) => state.place.centerCoordinates
  );

  const { markers } = useAppSelector((state) => state.placeMarkersSlice);

  const layers = useAppSelector((state) => state.placeLayers.layers);
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );

  const { isUnverfiedUserLoggedIn } = useUser();

  const toast = useToast();
  const { showUnverifiedModal } = useShowUnverifiedModal({ isNewPin: true });

  const defaultLat = mapRef?.getCenter?.()?.lat;
  const defaultLng = mapRef?.getCenter?.()?.lng;

  const checkIfInsidePolygon = async () => {
    const isPinInsidePolygon = await isAnyMarkerInsidePolygon(
      defaultLng,
      defaultLat,
      layers,
      gj
    );

    if (!isPinInsidePolygon) {
      dispatch(setIsMarkerInsidePolygon(false)); // marker is outside the polygon show the toast and render the new page
      toast({
        description: translate("places.pinOutsideBoundary"),
        isClosable: true,
        duration: toastDuration,
        status: "warning",
        containerStyle: {
          marginBottom: "1 !important"
        }
      });
    } else {
      dispatch(setIsMarkerInsidePolygon(true));
    }
  };

  const handlePinCreation = async () => {
    dispatch(resetIsMarkerInsidePolygon());
    dispatch(resetMarker());
    dispatch(resetAnswers());
    dispatch(clearAllErrors());
    dispatch(resetMarkerError());

    mapRef?.closePopup?.();

    const newMarkerData = {
      lat: defaultLat,
      lng: defaultLng,
      id: "new",
      isEditable: false
    };
    EsriGeoCode.reverseGeocode({
      apikey: esriToken
    })
      ?.latlng(new L.LatLng(defaultLat, defaultLng))
      ?.run(async (error, result) => {
        if (error) {
          return;
        }

        if (symbols?.length === 1) {
          const symbol = symbols[0];
          await dispatch(
            setMarker({
              ...newMarkerData,
              label: symbol?.name,
              color: symbol?.color,
              value: symbol?.value,
              pinIcon: symbol?.category?.icon,
              categoryId: symbol?.id,
              address: result?.address?.LongLabel
            })
          );
        } else {
          await dispatch(
            setMarker({
              ...newMarkerData,
              address: result?.address?.LongLabel
            })
          );
        }
      });

    checkIfInsidePolygon();

    dispatch(setView("create-pin"));
    setActionsVisibility(!isDesktop);

    if (!isEmpty(centerCoordinates)) {
      mapRef?.setView(
        [centerCoordinates?.lat, centerCoordinates?.lng],
        centerCoordinates?.zoom
      );
    }
    dispatch(setPinDetails({}));
    dispatch(setMapCenterCoordinates({}));
  };

  const initiatePinCreation = async () => {
    handlePinCreation();
  };

  const onCreatePinButtonClick = useCallback(() => {
    if (showUnverifiedModal()) return;

    if (["edit-pin", "create-pin"].includes(rightPanelView) && isDesktop) {
      setNextModalAction(() => initiatePinCreation);
      openUnsavedModal();

      return;
    }

    initiatePinCreation();
  }, [mapRef, rightPanelView, isUnverfiedUserLoggedIn]);

  const onActivityFeedButtonClick = () => {
    if (["edit-pin", "create-pin"].includes(rightPanelView) && isDesktop) {
      setNextModalAction(() => showActivityFeed);
      openUnsavedModal();

      return;
    }

    showActivityFeed();
  };

  const showActivityFeed = async () => {
    // remove marker if the user has clicked on it
    dispatch(resetIsMarkerInsidePolygon());
    dispatch(resetAnswers());
    dispatch(clearAllErrors());
    dispatch(resetMarker());
    dispatch(resetMarkerError());
    dispatch(setView("activity-feed"));
    dispatch(setPinDetails({}));

    if (!isEmpty(centerCoordinates)) {
      mapRef?.setView(
        [centerCoordinates?.lat, centerCoordinates?.lng],
        centerCoordinates?.zoom
      );
    }
    dispatch(setMapCenterCoordinates({}));
    mapRef?.closePopup();
  };

  const interactionsProps = {
    activityRef,
    onOpen,
    id: wrapperComp.current,
    handleActivityFeedClick: onActivityFeedButtonClick,
    rightPanelProps: rightPanelProps
  };

  /**
   * On dropping the blue bubble
   * show confirm and cancel
   * dispatch the create pin event
   * hide the widgets
   */
  const handleDropPinMobile = () => {
    onCreatePinButtonClick();
    setInteractionsVisibility(false);
  };

  /**
   * Once you click on confirm
   *
   * 1. switch off the actions and
   * isMarkerInsidePolygon boolean -> determines the
   * visibility of the first level of action buttons ( confirm and cancel )
   * 2. open the drawer
   * 3. set the view
   */

  const handleConfirmPinMobile = async () => {
    // below logic needs to be checked when we are doing the restrict pins
    // dispatch(setIsMarkerInsidePolygon(false));
    setActionsVisibility(false);
    setInteractionsVisibility(true);
    if (!isEditMode) {
      onOpen();
      dispatch(setView("create-pin"));

      if (!isEmpty(centerCoordinates)) {
        mapRef?.setView([centerCoordinates.lat, centerCoordinates.lng], 10);
      }
    } else {
      const hasError = await validateSurveyQuestions();
      if (hasError && isMobile) {
        // opens the drawer
        onOpen();
        dispatch(setView("edit-pin"));
        return;
      }
      await editMarker(marker);
    }
    dispatch(setPinDetails({}));
    dispatch(setMapCenterCoordinates({}));
  };

  /**
   * Once you click on cancel
   *
   * 1. reset the marker data
   * 2. set the view back to activity feed
   * 3. turn on the interaction icon
   * 4. turn off actions and isMarkerInsidePolygon boolean
   */
  const handleCancelPinMobile = () => {
    dispatch(resetIsMarkerInsidePolygon());
    dispatch(resetMarker());
    dispatch(resetFilterCategories());

    dispatch(setView("activity-feed"));
    setInteractionsVisibility(true);
    setActionsVisibility(false);

    if (!isEmpty(centerCoordinates)) {
      mapRef?.setView([centerCoordinates.lat, centerCoordinates.lng], 10);
    }
    dispatch(setPinDetails({}));
    dispatch(setMapCenterCoordinates({}));
  };
  const { login } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );
  const heightOfMap = login ? "135px" : "127px";
  const navbarElement = document.getElementsByClassName(
    "ehq-participant-header"
  )[0];
  const height = navbarElement ? `calc(100vh - ${heightOfMap})` : "100vh";

  const panel = useCallback(() => {
    return (
      <>
        <Box
          display={{ base: "block", lg: "block", sm: "none" }}
          width="30vw"
          height={isMobile ? "90svh" : height}
          id="pins-list-on-desktop"
          background={"gray.100"}
          {...rest}
        >
          {children}
        </Box>
        {showInteractions && (
          <Interactions
            handlePinClick={() => onCreatePinButtonClick()}
            handleDropPinMobile={() => handleDropPinMobile()}
            setActionsVisibility={setActionsVisibility}
            {...interactionsProps}
          />
        )}

        {isActionsVisible && isPinInsidePolygon && !isDesktop && (
          <MobilePinActions
            handleConfirmPinMobile={handleConfirmPinMobile}
            handleCancelPinMobile={handleCancelPinMobile}
          />
        )}
        <EsriDrawer
          isOpen={isOpen}
          handleCloseDrawer={handleCloseDrawer}
          setInteractionsVisibility={setInteractionsVisibility}
          handleActivityFeedClick={onActivityFeedButtonClick}
          rightPanelProps={rightPanelProps}
        >
          {children}
        </EsriDrawer>
      </>
    );
  }, [
    isActionsVisible,
    showInteractions,
    mapRef,
    children,
    isDesktop,
    isOpen,
    isPinInsidePolygon,
    handleCloseDrawer,
    markers,
    marker,
    isUnverfiedUserLoggedIn,
    rightPanelView
  ]);

  useEffect(() => {
    const handleKeyDown: any = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (["edit-pin", "create-pin"].includes(rightPanelView) && isDesktop) {
          setNextModalAction(() => showActivityFeed);
          openUnsavedModal();

          return;
        } else if (["view-pin-details"].includes(rightPanelView)) {
          showActivityFeed();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [rightPanelView]);

  return (
    <>
      {panel()}
      {isUnverifiedModalOpen && (
        <UnverifiedUserModal
          isOpen={isUnverifiedModalOpen}
          onClose={setUnverifiedModalOpen}
          handlePinCreation={handlePinCreation}
        />
      )}
    </>
  );
};

export default memo(EsriSidePanel);
